import { Suspense, useEffect, useState } from "react";
import ThreeMap from "./component/map";
import "./App.css";
import { Canvas } from "@react-three/fiber";
import { ModalWindow } from "./component/ModalWindow";
import {
  CalcelSimulationRequest,
  RainfallSettingArray,
  SimulationListType,
  RiverAndRainSensorID,
  PastRainDataListType,
  getPastFutureInputDataResponse,
  PlyFileURLListType,
} from "./types/type";
import Slider from "@mui/material/Slider";
import {
  cancelSimulation,
  getRiverLevelAndRainfall,
  getSimulationInputs,
  getSimulationResults,
  getSimulations,
  runSimulation,
  useAuth0Token,
  insertPastRainData,
} from "./utils/http-requests";
import { LoadingCircle } from "./component/LoadingCircle";
import { useAuth0 } from "@auth0/auth0-react";
import { FileDataProvider, useFileData } from "./component/FileDataContext";
import ResizableImage from "./component/ResizableImage";

export default function App() {
  //認証トークン
  const { getToken } = useAuth0Token();
  const { user } = useAuth0();

  // ログアウト
  const { isAuthenticated, logout } = useAuth0();
  const handleLogout = async () => {
    try {
      if (isAuthenticated) {
        // logout({ returnTo: window.location.origin });
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    } catch (e) {
      alert(e);
    }
  };

  //ローディング中のcircle表示予定
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //simulation list
  const [simulationList, setSimulationList] = useState<SimulationListType>();
  // ply file url list
  const [plyFileURLList, setPlyFileURLList] = useState<PlyFileURLListType>();

  const [isSettingWindowOpen, setIsSettingWindowOpen] =
    useState<boolean>(false);

  const [isSidebarHidden, setIsSidebarHidden] = useState<boolean>(false);

  const [referenceDateTime, setReferenceDateTime] = useState<Date>(new Date());

  const [pastWaterLevel, setPastWaterLevel] = useState<any>();

  const { setFileData } = useFileData();

  //シミュレーション一覧を取得
  const getSimulationsList = async () => {
    setIsLoading(true);
    try {
      const data = await getSimulations(await getToken());
      setSimulationList(data.results);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  //過去水位、過去降雨量を取得
  const getPastData = async () => {
    setIsLoading(true);
    const requestParams: RiverAndRainSensorID = {
      // ※お客様の水位と雨量のセンサーIDをここに設定する。
      river_sensor_id: "dummy_sensor_id",
      rain_sensor_id: "dummy_sensor_id",
    };
    try {
      const data = await getRiverLevelAndRainfall(
        await getToken(),
        requestParams
      );

      // 過去の最大時間を取得
      const maxPastTime = new Date(
        Math.max(
          ...data.results.RainFallInfoList.map((obj: any) =>
            new Date(obj.data_create_time).getTime()
          )
        )
      );
      // 未来の最小時間を計算（過去の最大時間 + 1時間）
      const minFutureTime = new Date(maxPastTime.getTime() + 60 * 60 * 1000);
      console.log("★maxPastTime = ", maxPastTime);

      setPastWaterLevel(
        [data.results.RiverLevelInfo].map((obj: any) => {
          if (obj.latest_river_level !== null) {
            return {
              data_create_time: obj.latest_data_time,
              input_data: obj.latest_river_level,
              sensor_id: obj.sensor_id,
              input_kbn: "1",
              river_or_rain_kbn: "1",
            };
          } else {
            return {
              data_create_time: obj.latest_data_time,
              input_data: NaN,
              sensor_id: obj.sensor_id,
              input_kbn: "2",
              river_or_rain_kbn: "1",
            };
          }
        })
      );
      setPastRainfall(
        data.results.RainFallInfoList.map((obj: any) => {
          if (obj.past_rain_amount !== null) {
            return {
              data_create_time: obj.data_create_time,
              input_data: obj.past_rain_amount,
              sensor_id: obj.sensor_id,
              input_kbn: "1",
              river_or_rain_kbn: "2",
            };
          } else {
            return {
              data_create_time: obj.data_create_time,
              input_data: NaN,
              sensor_id: obj.sensor_id,
              input_kbn: "2",
              river_or_rain_kbn: "2",
            };
          }
        })
      );

      // 未来の最小時間を利用して未来の雨量データリストを設定
      const futureRainfallList = [];
      for (let i = 0; i < 6; i++) {
        futureRainfallList.push({
          input_after_hour: formatDate(new Date(minFutureTime.getTime() + i * 60 * 60 * 1000)),
          input_rain_amount: NaN,
        });
      }
      setDataListState(futureRainfallList);
      // console.log("★DB＞過去雨量 = ", data.results.RainFallInfoList);
      // console.log("★pastRainFall = ", pastRainfall);
    } catch {
      console.error("error");
    } finally {
      setIsLoading(false);
    }
  };

  //各シミュレーションで使用した降雨量を取得
  const getSimulationData = async (id: string) => {
    setIsLoading(true);
    try {
      const data = await getSimulationInputs(await getToken(), id);
      setDataListState(data.results.rain_fall_infos);
    } catch {
      console.error("error");
    } finally {
      setIsLoading(false);
    }
  };

  //各シミュレーションで使用した過去水位・雨量／未来（予測）雨量を取得
  const getPastFutureInputData = async (id: string) => {
    setIsLoading(true);
    try {
      const data = await getSimulationInputs(await getToken(), id);
      // setPastFutureData(data.results);
      // console.log("★data.results.rain_fall_infos = ", data.results.rain_fall_infos);
      // console.log("★data.results.past_river_and_rain_data.RiverLevelInfo = ", data.results.past_river_and_rain_data.RiverLevelInfo);
      // console.log("★data.results.past_river_and_rain_data.RainFallInfoList = ", data.results.past_river_and_rain_data.RainFallInfoList);
      setDataListState(data.results.rain_fall_infos);
      setPastWaterLevel(
        [data.results.past_river_and_rain_data.RiverLevelInfo].map(
          (obj: any) => {
            return {
              data_create_time: obj.latest_data_time,
              input_data: obj.latest_river_level,
              sensor_id: obj.sensor_id,
              input_kbn: obj.input_kbn,
              river_or_rain_kbn: obj.river_or_rain_kbn,
            };
          }
        )
      );
      setPastRainfall(data.results.past_river_and_rain_data.RainFallInfoList);
    } catch {
      alert(
        "正しいデータが取得できませんでした。時間をおいて再度お試しください。"
      );
      setIsSettingWindowOpen(false);
      console.error("error");
    } finally {
      setIsLoading(false);
    }
  };

  //シミュレーション実行
  const postRunSimulation = async (
    rainfall: RainfallSettingArray,
    pastData: any
  ) => {
    setIsLoading(true);

    // 2024/10/17　水位に「28」を入力すると「28.000000000000004」に表示される不具合の修正 add start↓
    // 過去水位データの整形（cm⇒mに変換）
    // console.log("★pastData_before = ", pastData);
    pastData = pastData.map((data: any) => {
      // 水位データ（river_or_rain_kbnが"1"）の場合のみcmをmに変換
      if (data.river_or_rain_kbn === "1") {
        return {
          ...data, // 既存のデータをコピー
          input_data: data.input_data / 100, // 水位データをcmからmに変換
        };
      }
      return data; // そのまま返す（雨量データの場合）
    });
    // console.log("★pastData_after = ", pastData);
    // 2024/10/17　水位に「28」を入力すると「28.000000000000004」に表示される不具合の修正 add end↑

    const requestParams: any = {
      rain_fall_infos: rainfall,
      past_river_and_rain_data: pastData,
    };
    
    try {
      const data = await runSimulation(await getToken(), requestParams);
      console.log(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  //シミュレーションキャンセル
  const postCancelSimulation = async (simuId: string) => {
    setIsLoading(true);
    const requestParams: CalcelSimulationRequest = {
      simu_id: simuId,
    };
    try {
      const data = await cancelSimulation(await getToken(), requestParams);
      console.log(data);
    } catch {
      console.error("error");
    } finally {
      setIsLoading(false);
    }
  };

  //plyファイル取得
  const getSimulationPlyData = async (simuId: string) => {
    // setIsLoading(true);
    try {
      const data = await getSimulationResults(await getToken(), simuId);
      console.log(data);
      setPlyFileURLList(data.results);
      return data;
    } catch {
      console.error("error");
      alert("結果を取得できませんでした。");
    } finally {
      // setIsLoading(false);
    }
  };

  //過去雨量データ登録
  const postPastRainDataInsert = async (
    pastRainDataList: PastRainDataListType
  ) => {
    setIsLoading(true);
    const requestParams: any = {
      past_rain_data_list: pastRainDataList,
    };
    try {
      const data = await insertPastRainData(await getToken(), requestParams);
      console.log(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // fire only onload
  useEffect(() => {
    (async () => {
      await getSimulationsList();
      await getPastData();
      setIsSettingWindowOpen(false);
    })();
  }, []);

  // after referenceTime is set.
  useEffect(() => {
    setDataListState(initialDataList);
    setPastRainfall(pastRainfall);
  }, [referenceDateTime]);

  const roundDate = (date: Date): Date => {
    const roundedDate = new Date(date.getTime());
    const minutes = date.getMinutes();

    if (minutes >= 31) {
      roundedDate.setHours(date.getHours() + 1);
    }
    roundedDate.setMinutes(0);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    return roundedDate;
  };
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");

    return `${year}-${month}-${day} ${hour}:00`;
  };
  const currentReferenceTime = formatDate(roundDate(referenceDateTime));

  const getFutureTimeList = () => {
    const roundedDate = new Date(roundDate(referenceDateTime));
    roundedDate.setHours(roundedDate.getHours() + 1);

    const dates = [];
    for (let i = 0; i < 6; i++) {
      dates.push({
        input_after_hour: formatDate(roundedDate),
        input_rain_amount: NaN,
      });
      roundedDate.setHours(roundedDate.getHours() + 1);
    }
    return dates;
  };

  // for showing the data on the chart
  const initialDataList = getFutureTimeList();
  const [dataListState, setDataListState] =
    useState<RainfallSettingArray>(initialDataList);
  const [pastRainfall, setPastRainfall] = useState<RainfallSettingArray>();
  const [pastFutureData, setPastFutureData] =
    useState<getPastFutureInputDataResponse>();

  // for Yaxis domain
  const [maxRainfall, setMaxRainfall] = useState<any>(100);
  const [maxFutureRainfall, setMaxFutureRainfall] = useState<any>(100);
  useEffect(() => {
    // pastRainfallの中から最大値を取得
    const pastRainfallArray = pastRainfall?.map((obj: any) => obj.input_data);
    const maxPastRainfall = Math.max(...(pastRainfallArray ?? [100, 100]));
    // dataListStateの中から最大値を取得
    const dataListStateArray = dataListState.map(
      (obj) => obj.input_rain_amount
    );
    const maxDataListState = Math.max(...dataListStateArray);
    // それぞれの最大値を比較し、大きい方をmaxRainfallに設定
    // setMaxRainfall(Math.max(maxPastRainfall, maxDataListState));
    setMaxRainfall(maxPastRainfall);
    setMaxFutureRainfall(maxDataListState);
  }, [dataListState, pastRainfall]);

  // for label on the Slider
  // string型のListを設定するtime_slotというstateを作成
  // そのstateをSliderのlabelに設定する
  // そのstateを変更することで、ThreeMapに渡すplyファイルを変更する
  const [timeSlot, setimeSlot] = useState<string[]>([]);

  let marks = () => {
    let result: any[] = [];
    for (let i = 0; i < 6; i++) {
      result.push({
        value: i,
        label: timeSlot[i] || "0",
      });
    }
    return result;
  };

  const [modalType, setModalType] = useState<"pastFutureData" | "simulation">(
    "simulation"
  );

  const { fileData } = useFileData();
  const [sliderValue, setSliderValue] = useState(0); // スライダーの値を状態として保持
  const [viewMode, setViewMode] = useState("2D"); // 3Dモードか2Dモードかを状態として保持
  // スライダーの値が変更された際に呼ばれるコールバック関数
  const handleSliderChange = (e: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number); // スライダーの値を更新
  };
  // ２Dモードか３Dモードかを変更する際に呼ばれるコールバック関数
  const handleViewModeChange = (e: any) => {
    setViewMode(e.target.value);
  };
  // const [initialImageLoaded, setInitialImageLoaded] = useState(false);

  // useEffect(() => {
  //   if (!initialImageLoaded && fileData.pngFiles[0]) {
  //     const url = URL.createObjectURL(fileData.pngFiles[0]);
  //     setInitialImageLoaded(true);
  //     return () => {
  //       URL.revokeObjectURL(url); // Clean up the URL when component unmounts
  //     };
  //   }
  // }, [fileData.pngFiles, initialImageLoaded]);
  useEffect(() => {
    console.log("★sliderValue = ", sliderValue);
    console.log("★viewMode = ", viewMode);
    if (fileData.pngFiles[sliderValue]) {
      const url = URL.createObjectURL(fileData.pngFiles[sliderValue]);
      return () => {
        URL.revokeObjectURL(url); // Clean up the URL when component unmounts
      };
    }
  }, [fileData.pngFiles, viewMode, sliderValue]);

  return (
    <div className="App" id="App">
      {(fileData.floodFiles && fileData.floodFiles.length === 6) ||
      fileData.landFile ||
      fileData.buildingFile ? (
        <></>
      ) : (
        <div className="h-[100vh] w-[100vw] bg-gray-700 absolute top-0 left-0"></div>
      )}

      {/** you can specify the time here */}
      {/* <div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 w-[50vw] 
      bg-settingWindowBg py-2 px-8 rounded-md shadow-md"
      >
        <Slider
          aria-label="time"
          defaultValue={3}
          valueLabelDisplay="auto"
          step={1}
          marks={marks()}
          min={1}
          max={6}
        />
      </div> */}

      {/** header */}
      <div className="absolute top-0 bg-ArithmerBlue w-full h-[70px] flex items-center pl-8 justify-between">
        <div className="flex items-center">
          <img src="./img/Arithmer_logo.png" className="h-[60px]" />
          <p className="text-[20px] text-white ml-8">
            浅見川浸水予測AIシステム
          </p>
        </div>
        <div
          className="cursor-pointer flex items-center h-[70px] border-l-2 border-white px-4"
          onClick={handleLogout}
        >
          <img src="./img/logout.png" className="h-[40px]" />
          <div className="text-white text-[20px]">Logout</div>
        </div>
      </div>

      {/** loader */}
      {isLoading && <LoadingCircle />}

      <div className="">
        {/** sidebar */}
        <div
          className={`${
            isSidebarHidden && "-translate-x-full"
          } absolute left-0 top-[70px] bg-settingWindowMain w-[270px] 
          h-[300px] flex flex-col items-center p-1 shadow-md
          transition-all`}
        >
          <div
            onClick={() => setIsSidebarHidden(!isSidebarHidden)}
            className="absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer translate-x-5 text-xs h-[60px] 
              bg-settingWindowBg flex items-center rounded-tr-md rounded-br-md px-1"
          >
            {isSidebarHidden ? "▶" : "◀"}
          </div>

          <div className="text-white text-planeText w-[100%] h-[20%] items-center flex flex-col justify-center">
            <img src="./img/rain.png" className="w-[20px]" />
            <p>メニュー</p>
          </div>
          <div className="bg-settingWindowBg w-[100%] h-[80%] flex flex-col items-center p-6">
            {/* <div className="text-left w-[100%]">
              <h4 className="text-planeText text-settingWindowMain font-bold dark:text-white">
                降雨モデル
              </h4>
              <p className="text-gray-400 text-[0.5vw] mt-2">
                調整後現在時刻：{currentReferenceTime}
              </p>
            </div> */}
            <button
              type="button"
              disabled={
                !(user && user["https://hirono-flood-sim/roles"][0] === "exec")
              }
              className={`${"hover:bg-ArithmerBlue focus:ring-4 focus:ring-purple-300 dark:hover:bg-purple-700 dark:focus:ring-purple-900"} mt-[10%] w-[100%] flex items-center justify-between text-planeText h-[60px] focus:outline-none text-white bg-settingWindowMain font-medium rounded-lg text-planeText px-5 py-2.5 mb-2 dark:bg-purple-600 `}
              onClick={() => {
                (async () => {
                  setFileData({
                    floodFiles: [],
                    landFile: null,
                    buildingFile: null,
                    pngFiles: [],
                  });
                  await getPastData();
                  setIsSettingWindowOpen(true);
                  setModalType("pastFutureData");
                })();
              }}
            >
              <div
                className="flex items-center"
                onClick={() => {
                  setReferenceDateTime(new Date());
                }}
              >
                <img src="./img/spanner.png" className="w-[30px]"></img>
                <p className="pl-2">シミュレーション実行</p>
              </div>
              <img src="./img/arrow_02.png" className="w-[15px]" />
            </button>
            <button
              type="button"
              onClick={() => {
                setFileData({
                  floodFiles: [],
                  landFile: null,
                  buildingFile: null,
                  pngFiles: [],
                });
                setIsSettingWindowOpen(true);
                setReferenceDateTime(new Date());
                setModalType("simulation");
                setViewMode("2D");
                setSliderValue(0);
              }}
              className="w-[100%] flex items-center justify-between text-planeText focus:outline-none text-white bg-settingWindowMain 
              hover:bg-ArithmerBlue focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-planeText px-5 py-2.5 mb-2 dark:bg-purple-600 
              dark:hover:bg-purple-700 dark:focus:pg-purple-900 h-[60px]"
            >
              <div className="flex items-center">
                <img src="./img/bar-chart.png" className="w-[25px]"></img>
                <p className="pl-2">シミュレーション一覧</p>
              </div>
              <img src="./img/arrow_02.png" className="w-[15px]" />
            </button>
          </div>
        </div>
        {/*モーダル*/}
        {isSettingWindowOpen && (
          <ModalWindow
            setIsSettingWindowOpen={setIsSettingWindowOpen}
            currentReferenceTime={currentReferenceTime}
            dataListState={dataListState}
            maxRainfall={maxRainfall}
            maxFutureRainfall={maxFutureRainfall}
            // error={error}
            // setError={setError}
            modalType={modalType}
            setDataListState={setDataListState}
            setModalType={setModalType}
            // savedData={savedData}
            // setSavedData={setSavedData}
            getSimulationsList={getSimulationsList}
            getSimulationData={getSimulationData}
            getPastFutureInputData={getPastFutureInputData}
            simulationList={simulationList}
            plyFileURLList={plyFileURLList}
            setIsLoading={setIsLoading}
            setimeSlot={setimeSlot}
            postCancelSimulation={postCancelSimulation}
            getSimulationPlyData={getSimulationPlyData}
            postRunSimulation={postRunSimulation}
            postPastRainDataInsert={postPastRainDataInsert}
            // referenceTime={referenceTime}
            getPastData={getPastData}
            pastWaterLevel={pastWaterLevel}
            pastRainfall={pastRainfall}
            setPastWaterLevel={setPastWaterLevel}
            setPastRainfall={setPastRainfall}
            pastFutureData={pastFutureData}
            setPastFutureData={setPastFutureData}
            setReferenceDateTime={setReferenceDateTime}
            user={user}
          />
        )}
      </div>
      {/* 下記のSliderとCanvasはfileDataにfloodFilesやlandFileやbuildingFileの設定がない場合、非表示にする */}
      {fileData.floodFiles.length === 6 ||
      fileData.landFile ||
      fileData.buildingFile ? (
        <>
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 w-[70vw] 
          bg-settingWindowBg py-2 px-8 rounded-md shadow-md"
          >
            <span>表示モード：</span>
            <label>
              <input
                type="radio"
                value="2D"
                checked={viewMode === "2D"}
                onChange={handleViewModeChange}
              />
              2D
            </label>
            <span className="mx-2"></span>
            <label>
              <input
                type="radio"
                value="3D"
                checked={viewMode === "3D"}
                onChange={handleViewModeChange}
              />
              3D
            </label>
            {/** you can specify the time here */}
            <Slider
              aria-label="time"
              value={sliderValue} // 状態からスライダーの値を取得
              onChange={handleSliderChange} // スライダーの変更時に呼ばれるコールバック関数
              valueLabelDisplay="auto"
              step={1}
              marks={marks()}
              min={0}
              max={5}
              style={{ width: "90%" }}
            />
          </div>
          <div className="h-[70px]">
            {/* 2Dモードの表示 */}
            {viewMode === "2D" && fileData.pngFiles[sliderValue] && (
              <ResizableImage fileData={fileData} sliderValue={sliderValue} />
            )}
            {viewMode === "3D" && (
              <Canvas>
                <Suspense fallback={null}>
                  <ThreeMap
                    floodFile={fileData.floodFiles[sliderValue]}
                    landFile={fileData.landFile}
                    buildingFile={fileData.buildingFile}
                  />
                </Suspense>
              </Canvas>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
